'use client';

/**
 * Workspace-canvas reconciliation for delegation events emitted by the
 * agent runtime.
 *
 *   start    → if the same delegated agent is ALREADY on canvas under
 *              this hosting root, REUSE that item: just flip its
 *              `delegationStatus` back to `'in_progress'`, refresh the
 *              callId / sub-chat id, and let the edge dots resume.
 *              Otherwise tear down any sibling delegation pucks under
 *              this hosting root and spawn a NEW collapsed agent puck
 *              to the right with `cache.delegationStatus =
 *              'in_progress'` and a tagged provenance edge.
 *   complete → patch the existing puck to `delegationStatus = 'complete'`,
 *              which freezes the edge animation in the static state.
 *   error    → patch to `'error'` and record the `errorMessage`.
 *
 * Replace-on-new (with reuse): when the user delegates to a NEW agent
 * we wipe the prior chain so the canvas reflects the active
 * conversation, not the history. Re-asking the SAME delegated agent
 * keeps that widget mounted — important because the user may have
 * expanded the puck into a full chat or moved it; tearing it down
 * every round would be jarring AND would skip the edge dot animation
 * the user expects to see on each new round.
 *
 * Race protection (commits in code):
 *   - `seenCallIds: Set<string>` short-circuits a re-emitted `start`
 *     for the same callId. Without this a UAMP reconnect would dupe
 *     the puck.
 *   - The tear-down + spawn sequence is awaited so the server sees
 *     DELETE → POST in order. Without sequencing the spawn can land
 *     before the delete completes and produce orphans.
 *   - `complete` / `error` events whose callId no longer maps to a
 *     live item (because a replay or new start tore down the chain)
 *     are silently dropped.
 *
 * Mounted by `AgentNode`. The hook is a no-op when `hostingAgentItemId`
 * is not provided.
 */

import { useEffect, useRef } from 'react';
import {
  subscribeDelegationEvents,
  type DelegationEvent,
} from '@/lib/agents/delegation-events';
import {
  resolveParticipant,
  getCachedParticipant,
} from '@/lib/workspaces/resolve-participant';
import { useWorkspaceActions, useWorkspaceItems } from './store';

export interface UseDelegationVisualizationOpts {
  /** Workspace item id of the AgentNode owning the chat that emits
   *  events. Required — without it the hook is a no-op. */
  hostingAgentItemId: string;
  /** Root of the chain (for nested cases this is the topmost agent
   *  the user originally talked to). Spawned pucks are tagged with
   *  this so an outer-level `start` can tear down the whole chain. */
  rootHostingAgentItemId?: string;
}

export function useDelegationVisualization({
  hostingAgentItemId,
  rootHostingAgentItemId,
}: UseDelegationVisualizationOpts): void {
  const items = useWorkspaceItems();
  const { spawnChild, patchItemState, removeItem } = useWorkspaceActions();

  // Match `.ws-delegation-puck.ws-delegation-exit { animation: 220ms }`
  // in globals.css. Keep this in sync if the CSS duration changes —
  // a shorter wait clips the animation; longer is harmless but
  // delays a follow-up spawn since the chain queue serialises
  // DELETE→POST.
  const EXIT_ANIM_MS = 240;

  // Puck + expanded geometry — must match `agent-node.tsx`'s
  // `COLLAPSED_W / COLLAPSED_H` and the default agent-widget size
  // it expands to. Centralised here so a new delegation spawn lands
  // with the same bbox the user would see after a manual collapse,
  // which is what keeps the cardinal connector handles flush against
  // the avatar ring (see the `.ws-node-frame[data-collapsed='true']`
  // overrides in globals.css). Kept as module-local constants —
  // they're tiny and hard-coding them mirrors how `toggleCollapsed`
  // already does it; if either side drifts the puck visibly breaks
  // and the tests covering the geometry will catch it.
  const PUCK_W = 64;
  const PUCK_H = 92;
  const EXPANDED_W = 360;
  const EXPANDED_H = 480;

  // Live snapshots of items + hook config so the subscription
  // callback never closes over a stale value (we register once and
  // tear down on unmount, not on every render).
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const hostingRef = useRef(hostingAgentItemId);
  hostingRef.current = hostingAgentItemId;
  const rootRef = useRef(rootHostingAgentItemId ?? hostingAgentItemId);
  rootRef.current = rootHostingAgentItemId ?? hostingAgentItemId;

  // De-dupe across reconnects + replays. Lives for the lifetime of
  // the AgentNode mount; persisting across mounts is unnecessary
  // because the spawned items themselves are the source of truth and
  // the hook reads from the live `items` snapshot before any spawn.
  const seenCallIds = useRef<Set<string>>(new Set());

  // Per-chain operation queue. Tear-down + spawn for a given
  // `rootHostingAgentItemId` runs serially so the server never sees
  // POST-before-DELETE for the same logical chain. Keyed by root id
  // so independent chains (different AgentNodes, future shared
  // workspaces) don't block each other.
  const chainQueues = useRef<Map<string, Promise<void>>>(new Map());

  useEffect(() => {
    if (!hostingAgentItemId) return;

    const enqueue = (rootId: string, work: () => Promise<void>): Promise<void> => {
      const prev = chainQueues.current.get(rootId) ?? Promise.resolve();
      const next = prev.then(work).catch((err) => {
        // Queue failures must not poison the chain — log and continue.
        // eslint-disable-next-line no-console
        console.error('[delegation-viz] chain queue work failed', err);
      });
      chainQueues.current.set(rootId, next);
      return next;
    };

    const unsubscribe = subscribeDelegationEvents((event: DelegationEvent) => {
      // Scope: only react to events emitted by THIS hosting agent's
      // chat. Other AgentNodes mount their own copy of the hook and
      // receive the events relevant to them.
      if (event.hostingAgentItemId !== hostingRef.current) return;

      if (event.phase === 'start') {
        // Replay events (clicked historical tool-call rows) bypass the
        // de-dupe so the same call can be opened from chat history any
        // number of times. Live events still go through the dedupe to
        // guard against UAMP reconnect re-emissions.
        if (!event.isReplay) {
          if (seenCallIds.current.has(event.callId)) return;
          seenCallIds.current.add(event.callId);
        }

        const rootId = event.rootHostingAgentItemId || rootRef.current;
        const delegated = event.delegated;

        void enqueue(rootId, async () => {
          // 0. Reuse path — same delegated agent already on canvas
          //    under this hosting root? Just refresh its state and
          //    return; tearing down + respawning would discard any
          //    user-driven expansion / position, and the edge dot
          //    animation already restarts when the cached edge
          //    `data.status` flips from `complete` back to
          //    `in_progress` (see workspace-canvas edge rebuild).
          //
          //    We match on (relation === 'delegation', username) and
          //    NOT on `delegationOriginAgentItemId` — across reloads
          //    a parent widget's item id can drift while its puck
          //    children retain the OLD origin tag. Matching by
          //    username keeps reuse stable in that case, which is the
          //    scenario where the user has expanded a delegation
          //    puck into a real chat widget and re-asks the same
          //    agent.
          const existing = itemsRef.current.find((it) => {
            if (it.type !== 'agent') return false;
            const cache = it.state.cache as Record<string, unknown> | undefined;
            if (cache?.relation !== 'delegation') return false;
            return cache?.username === delegated.username;
          });
          if (existing) {
            const prevCache = (existing.state.cache ?? {}) as Record<string, unknown>;
            const existingChatId = existing.state.chatId ?? null;
            // On a *live* delegation event we only seed the sub-chat
            // id when the puck has no chat yet — the user may have
            // manually navigated the widget elsewhere, and we don't
            // want a runtime ping to yank them back.
            //
            // On a *replay* (user clicked a historical "talked to
            // @agent" chip), the click is explicit intent to view
            // THAT sub-chat — always honor it, even if the widget
            // currently shows a different conversation. Without this
            // override the click is a no-op against an already-mounted
            // delegate widget and the user (rightly) reports
            // "nothing happens". Same reason we force-expand below:
            // a collapsed puck quietly absorbing the click is
            // indistinguishable from the click being lost.
            const shouldSwitchChat = event.isReplay
              ? event.subChatId !== undefined
              : !existingChatId;
            const shouldExpand = event.isReplay && prevCache.collapsed === true;
            try {
              patchItemState(existing.id, {
                ...(shouldSwitchChat
                  ? { chatId: event.subChatId ?? null }
                  : {}),
                cache: {
                  ...prevCache,
                  // Force the puck open on explicit user replay so the
                  // sub-chat is actually visible, not hidden behind the
                  // collapsed avatar. Live events leave collapsed state
                  // untouched.
                  ...(shouldExpand ? { collapsed: false } : {}),
                  // Replay clicks revisit a delegation that ALREADY
                  // finished — land directly in `complete` so the edge
                  // renders as a static line. Setting `in_progress`
                  // here briefly (until the follow-up `complete` event
                  // patched it back) caused the dot animation to flash
                  // on every chip click. Live (non-replay) starts keep
                  // the running visual so an actively-streaming
                  // delegation reads correctly.
                  delegationStatus: event.isReplay ? 'complete' : 'in_progress',
                  delegationOriginAgentItemId: rootId,
                  delegationCallId: event.callId,
                  delegationSubChatId: event.subChatId,
                  delegationCompletedAt: event.isReplay ? Date.now() : undefined,
                  delegationErrorMessage: undefined,
                  delegationExiting: undefined,
                  // Bump on every fresh LIVE start so workspace-canvas
                  // sees a cache change even when every other field
                  // happens to match — keeps the edge rebuild + the
                  // dot animation restart deterministic. Replay
                  // skips the bump because there's no animation to
                  // restart.
                  ...(event.isReplay ? {} : { delegationStartedAt: Date.now() }),
                },
              });
              // Surface the focus request to whoever is interested
              // (typically the AgentNode hosting this delegate's
              // hosting agent) so they can scroll/center the
              // existing puck into view. Without this a reused widget
              // far offscreen will silently absorb the click — the
              // user sees nothing. Best-effort: in test envs that
              // stub window we just skip.
              if (event.isReplay && typeof window !== 'undefined') {
                try {
                  window.dispatchEvent(
                    new CustomEvent('ws:focus-item', {
                      detail: { itemId: existing.id, expand: shouldExpand },
                    }),
                  );
                } catch {
                  // best-effort
                }
              }
            } catch (err) {
              // eslint-disable-next-line no-console
              console.warn('[delegation-viz] reuse patch failed', err);
            }
            return;
          }

          // 1. Tear down any prior delegation pucks rooted at the
          //    same agent so the canvas shows the *active* chain
          //    only. Sequenced (not Promise.all racing the spawn)
          //    to guarantee DELETE-before-POST ordering on the
          //    server.
          const stale = itemsRef.current.filter((it) => {
            const cache = it.state.cache as Record<string, unknown> | undefined;
            return (
              cache?.relation === 'delegation' &&
              cache?.delegationOriginAgentItemId === rootId
            );
          });
          if (stale.length > 0) {
            // Mark every stale puck with `delegationExiting` first so
            // workspace-canvas re-renders them with the exit className
            // and the CSS keyframe gets a chance to play. Then sleep
            // for the animation duration before actually removing the
            // items — keeps the disappearance smooth instead of an
            // abrupt unmount.
            for (const it of stale) {
              const prev = (it.state.cache ?? {}) as Record<string, unknown>;
              try {
                patchItemState(it.id, {
                  cache: { ...prev, delegationExiting: true },
                });
              } catch {
                // Best-effort; if the patch fails we still tear down
                // below — the user just sees a hard cut.
              }
            }
            await new Promise<void>((r) => setTimeout(r, EXIT_ANIM_MS));
            for (const it of stale) {
              try {
                await removeItem(it.id);
              } catch (err) {
                // Best-effort: a missing item is fine; rethrowing
                // would block the spawn below.
                // eslint-disable-next-line no-console
                console.warn('[delegation-viz] tear-down failed', err);
              }
            }
          }

          // 2. Resolve the delegated user → id so the store accepts
          //    the spawn (it validates a non-empty `refId` for
          //    `type=agent`). We prefer `delegated.id` from the event
          //    when present, falling back to a `/api/users/<handle>`
          //    lookup. The lookup is cached + de-duped across the tab
          //    so a burst of identical delegations only fires one
          //    request. If we still can't resolve a real id we drop
          //    the puck entirely — better than triggering a server
          //    400 every render cycle.
          let refId = delegated.id;
          if (!refId) {
            const resolved =
              getCachedParticipant(delegated.username) ??
              (await resolveParticipant(delegated.username));
            if (resolved) refId = resolved.id;
          }
          if (!refId) {
            // Forget the callId so a later event with a populated id
            // (e.g. a server-side enrichment) can spawn the puck.
            seenCallIds.current.delete(event.callId);
            // eslint-disable-next-line no-console
            console.warn(
              '[delegation-viz] could not resolve delegated user id for',
              delegated.username,
            );
            return;
          }

          // 3. Spawn the new direct child to the right of the
          //    hosting agent in collapsed (puck) mode.
          //
          //    Width / height MUST match the puck geometry constants
          //    in `agent-node.tsx::toggleCollapsed`. Without these
          //    the store falls back to `DEFAULT_SIZES.agent`
          //    (360x480), the avatar visually shrinks to a puck via
          //    `cache.collapsed`, but the underlying React Flow bbox
          //    stays 360x480. That mismatch is what made the
          //    connector handles attach somewhere out near the
          //    invisible bbox edges instead of flush on the avatar
          //    ring — and the handles only "snapped" to the right
          //    place after the user manually toggled collapse,
          //    because the toggle finally patched the size.
          //
          //    `expandedSize` is what `toggleCollapsed` restores to
          //    when the user expands the puck into a full chat
          //    widget. Seeding it here matches the size a freshly
          //    created agent gets from `defaultStateFor`, so the
          //    user-initiated expand lands in the same shape as a
          //    just-spawned agent.
          try {
            const spawned = await spawnChild({
              parentItemId: hostingRef.current,
              type: 'agent',
              refId,
              placement: 'right',
              state: {
                collapsed: true,
                width: PUCK_W,
                height: PUCK_H,
                expandedSize: { width: EXPANDED_W, height: EXPANDED_H },
                // Seed the sub-chat id so when the user expands the
                // puck they land directly in the live sub-conversation
                // instead of the agent's welcome screen. With chatId
                // pre-bound the embedded `ChatView` renders the
                // populated chat (messages + prompt anchored to the
                // bottom of real content) — keeps the prompt from
                // floating high in an otherwise empty widget.
                //
                // Must be a STRING or absent — the server schema is
                // `z.string().optional()` and `null` is rejected
                // (`Expected string, received null` 400). When the
                // delegation event arrives before the sub-chat id is
                // known (live `start` from the chip-replay path) we
                // omit the field entirely so the puck can spawn now
                // and bind to the chat once it's announced.
                ...(event.subChatId ? { chatId: event.subChatId } : {}),
                cache: {
                  username: delegated.username,
                  displayName: delegated.displayName ?? delegated.username,
                  avatarUrl: delegated.avatarUrl ?? null,
                  accountType: 'agent',
                  relation: 'delegation',
                  delegationOriginAgentItemId: rootId,
                  delegationCallId: event.callId,
                  // Replay spawns a puck for a delegation that ALREADY
                  // finished — start in `complete` so the edge to the
                  // hosting agent renders as a static line. Live
                  // spawns keep `in_progress` so the dot animation
                  // plays while the delegation is actively streaming.
                  delegationStatus: event.isReplay ? 'complete' : 'in_progress',
                  delegationSubChatId: event.subChatId,
                  // Tag spawn for the CSS mount animation; cleared by
                  // the node frame after the animation completes so
                  // it doesn't replay on re-render.
                  delegationSpawnedAt: Date.now(),
                  // Bump `delegationStartedAt` only for live starts —
                  // it's what `DelegationEdge` keys the dot animation
                  // restart off of. Replay clicks should never trigger
                  // that animation.
                  ...(event.isReplay
                    ? { delegationCompletedAt: Date.now() }
                    : { delegationStartedAt: Date.now() }),
                },
              },
            });
            // Explicit user intent (chip click) → pan the viewport so
            // the fresh puck is centered + visible. Without this a
            // canvas already crowded with widgets quietly absorbs the
            // spawn off-screen and the user reports "the click did
            // nothing". Live (non-replay) starts don't focus —
            // forcibly yanking the viewport during a running stream
            // would fight whatever the user was looking at.
            if (
              spawned &&
              event.isReplay &&
              typeof window !== 'undefined'
            ) {
              try {
                window.dispatchEvent(
                  new CustomEvent('ws:focus-item', {
                    detail: { itemId: spawned.id, expand: true },
                  }),
                );
              } catch {
                // best-effort
              }
            }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[delegation-viz] spawn failed', err);
          }
        });
        return;
      }

      // `complete` / `error` patch the puck status. We funnel these
      // THROUGH the same per-root chain queue as `start` so a fast
      // back-to-back `start → complete` (typical for replay clicks,
      // where both fire synchronously from the same JS turn) is
      // observed AFTER the start's async spawn/reuse work has
      // landed. Running it synchronously here would consistently
      // race the spawn and silently drop the complete patch — the
      // puck would then stay stuck in `'in_progress'` forever.
      //
      // If the chain was genuinely torn down between `start` and
      // `complete` (e.g. a sibling replay overwrote it), the lookup
      // inside the queued work still misses and we silently drop —
      // verified against `itemsRef.current` so we don't patch a
      // stale closure snapshot.
      if (event.phase === 'complete' || event.phase === 'error') {
        const rootId = event.rootHostingAgentItemId || rootRef.current;
        void enqueue(rootId, async () => {
          const target = itemsRef.current.find((it) => {
            const cache = it.state.cache as Record<string, unknown> | undefined;
            return (
              cache?.relation === 'delegation' &&
              cache?.delegationCallId === event.callId
            );
          });
          if (!target) return;
          const prevCache = (target.state.cache ?? {}) as Record<string, unknown>;
          // Backfill `chatId` when the puck was spawned without one
          // (live `start` arrived before the sub-chat id was known)
          // and the `complete`/`error` event now carries it. Without
          // this the user expands the puck and lands in the agent's
          // welcome screen instead of the actual sub-conversation
          // that produced the result they're trying to inspect.
          // We never CLOBBER an existing chatId — the user may have
          // manually navigated the widget to a different chat.
          const needsChatIdBackfill =
            !target.state.chatId && typeof event.subChatId === 'string';
          try {
            patchItemState(target.id, {
              ...(needsChatIdBackfill ? { chatId: event.subChatId } : {}),
              cache: {
                ...prevCache,
                delegationStatus: event.phase === 'complete' ? 'complete' : 'error',
                delegationCompletedAt: Date.now(),
                ...(event.subChatId
                  ? { delegationSubChatId: event.subChatId }
                  : {}),
                ...(event.phase === 'error' && event.errorMessage
                  ? { delegationErrorMessage: event.errorMessage }
                  : {}),
              },
            });
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[delegation-viz] patch failed', err);
          }
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [hostingAgentItemId, spawnChild, patchItemState, removeItem]);
}
